import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfessionalHeader from '../components/ProfessionalHeader';
import MLRecommendationResults from '../components/MLRecommendationResults';
import WedgeRecommendationResults from '../components/WedgeRecommendationResults';
import {
  getProfileById,
  getDriverRecommendationsV9,
  getIronRecommendationsV9,
  getWedgeRecommendationsRuleBased,
  MLRecommendationsResponse,
} from '../services/recommendationService';
import toast from 'react-hot-toast';
import { logEvent, newSessionId } from '../services/telemetryService';

// ─── Read-only section helpers (mirrors ProfileReviewPage logic) ──────────────

interface ReviewField {
  label: string;
  value: any;
}

function getBasicInfoFields(d: any): ReviewField[] {
  return [
    { label: 'Handicap', value: d.handicap },
    { label: 'Age', value: d.age },
    { label: 'Height', value: d.height_inches ? `${d.height_inches}"` : null },
    { label: 'Years Playing', value: d.years_playing },
    { label: 'Rounds Per Year', value: d.rounds_per_year },
    { label: 'Dominant Hand', value: d.dominant_hand },
  ];
}

function getSwingFields(d: any): ReviewField[] {
  const driver = d.driver_swing_data || {};
  const iron = d.iron_swing_data || {};
  return [
    { label: 'Driver Swing Speed', value: (driver.swing_speed_driver || d.swing_speed_driver) ? `${driver.swing_speed_driver || d.swing_speed_driver} mph` : null },
    { label: 'Driver Attack Angle', value: (driver.attack_angle_driver || d.attack_angle_driver) ? `${driver.attack_angle_driver || d.attack_angle_driver}°` : null },
    { label: '7-Iron Swing Speed', value: (iron.swing_speed_7iron || d.swing_speed_7iron) ? `${iron.swing_speed_7iron || d.swing_speed_7iron} mph` : null },
    { label: 'Iron Attack Angle', value: (iron.attack_angle_irons || d.attack_angle_irons) ? `${iron.attack_angle_irons || d.attack_angle_irons}°` : null },
  ];
}

function getBallFlightFields(d: any): ReviewField[] {
  const driver = d.launch_monitor_driver || {};
  const iron = d.launch_monitor_iron || {};
  const wedge = d.launch_monitor_wedge || {};
  return [
    { label: 'Driver Launch Angle', value: driver.launch_angle_driver ? `${driver.launch_angle_driver}°` : null },
    { label: 'Driver Spin Rate', value: driver.spin_rate_driver ? `${driver.spin_rate_driver} rpm` : null },
    { label: 'Driver Ball Speed', value: driver.ball_speed_driver ? `${driver.ball_speed_driver} mph` : null },
    { label: 'Driver Carry', value: driver.driver_carry_distance ? `${driver.driver_carry_distance} yds` : null },
    { label: 'Driver Face-to-Path', value: driver.face_to_path_driver ? `${driver.face_to_path_driver}°` : null },
    { label: '7-Iron Launch', value: iron.launch_angle_7i ? `${iron.launch_angle_7i}°` : null },
    { label: '7-Iron Spin', value: iron.spin_rate_7i ? `${iron.spin_rate_7i} rpm` : null },
    { label: '7-Iron Ball Speed', value: iron.ball_speed_7i ? `${iron.ball_speed_7i} mph` : null },
    { label: '7-Iron Carry', value: iron.iron_carry_distance ? `${iron.iron_carry_distance} yds` : null },
    { label: 'Wedge Launch', value: wedge.launch_angle_wedge ? `${wedge.launch_angle_wedge}°` : null },
    { label: 'Wedge Spin', value: wedge.spin_rate_wedge ? `${wedge.spin_rate_wedge} rpm` : null },
    { label: 'Wedge Carry', value: wedge.wedge_carry_distance ? `${wedge.wedge_carry_distance} yds` : null },
  ];
}

function getPhysicalFields(d: any): ReviewField[] {
  const p = d.physical_profile || {};
  return [
    { label: 'Wrist to Floor', value: p.wrist_to_floor ? `${p.wrist_to_floor}"` : null },
    { label: 'Flexibility', value: p.flexibility_rating ? `${p.flexibility_rating}/10` : null },
    { label: 'Core Strength', value: p.core_strength ? `${p.core_strength}/10` : null },
    { label: 'Weekly Workouts', value: p.weekly_workouts },
    { label: 'Posture Type', value: p.posture_type },
  ];
}

function getEquipmentFields(d: any): ReviewField[] {
  const eq = d.equipment_history || {};
  const drv = eq.current_clubs_driver || {};
  const irons = eq.current_clubs_irons || {};
  const wedge = eq.current_clubs_wedges || {};
  return [
    { label: 'Driver Satisfaction', value: eq.current_driver_satisfaction ? `${eq.current_driver_satisfaction}/10` : null },
    { label: 'Current Driver', value: drv.brand && drv.model ? `${drv.brand} ${drv.model}` : null },
    { label: 'Driver Shaft', value: drv.shaft_flex && drv.shaft_model ? `${drv.shaft_model} (${drv.shaft_flex})` : null },
    { label: 'Iron Satisfaction', value: eq.current_iron_satisfaction ? `${eq.current_iron_satisfaction}/10` : null },
    { label: 'Current Irons', value: irons.brand && irons.model ? `${irons.brand} ${irons.model}` : null },
    { label: 'Iron Shaft', value: irons.shaft_flex && irons.shaft_model ? `${irons.shaft_model} (${irons.shaft_flex})` : null },
    { label: 'Wedge Satisfaction', value: eq.current_wedge_satisfaction ? `${eq.current_wedge_satisfaction}/10` : null },
    { label: 'Current Wedge', value: wedge.brand && wedge.model ? `${wedge.brand} ${wedge.model}` : null },
  ];
}

function getPlayingFields(d: any): ReviewField[] {
  const p = d.playing_profile || {};
  return [
    { label: 'Course Type', value: p.typical_course_type },
    { label: 'Primary Goal', value: p.primary_goal_structured },
    { label: 'Target Handicap', value: p.target_handicap },
    { label: 'Purchase Timeline', value: p.purchase_timeline },
    { label: 'Budget', value: d.preferences?.budget_range },
  ];
}

function getGoalsFields(d: any): ReviewField[] {
  const g = d.goals_challenges || {};
  return [
    { label: 'Primary Goal', value: g.primary_goal },
    { label: 'Biggest Frustration', value: g.biggest_frustration },
    { label: 'Practice Frequency', value: g.practice_frequency },
    { label: 'Lesson Frequency', value: g.lesson_frequency },
  ];
}

const SECTIONS = [
  { name: 'Basic Information', fn: getBasicInfoFields },
  { name: 'Swing Characteristics', fn: getSwingFields },
  { name: 'Ball Flight Data', fn: getBallFlightFields },
  { name: 'Physical Profile', fn: getPhysicalFields },
  { name: 'Equipment History', fn: getEquipmentFields },
  { name: 'Playing Profile & Goals', fn: getPlayingFields },
  { name: 'Goals & Challenges', fn: getGoalsFields },
];

// ─── Component ────────────────────────────────────────────────────────────────

type Tab = 'driver' | 'irons' | 'wedges';

const FittingHistoryPage: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<Tab>('driver');
  const [driverRecs, setDriverRecs] = useState<MLRecommendationsResponse | null>(null);
  const [ironRecs, setIronRecs] = useState<MLRecommendationsResponse | null>(null);
  const [wedgeRecs, setWedgeRecs] = useState<any>(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState<string | null>(null);

  const id = parseInt(profileId || '0', 10);
  const sessionIdRef = useRef<string>(newSessionId());

  // Log fitting history opened
  useEffect(() => {
    if (!id) return;
    logEvent({
      session_id: sessionIdRef.current,
      event_type: 'fitting_history_opened',
    });
  }, [id]);

  // Load profile on mount
  useEffect(() => {
    if (!id) return;
    getProfileById(id)
      .then((p) => {
        setProfile(p);
        // Log item viewed with age
        const createdAt = p?.created_at ? new Date(p.created_at) : null;
        const ageDays = createdAt
          ? Math.floor((Date.now() - createdAt.getTime()) / 86400000)
          : undefined;
        logEvent({
          session_id: sessionIdRef.current,
          event_type: 'fitting_history_item_viewed',
          metadata: { profile_id: id, age_days: ageDays },
        });
      })
      .catch((err) => {
        toast.error('Failed to load fitting profile');
        console.error(err);
      })
      .finally(() => setProfileLoading(false));
  }, [id]);

  // Load driver recs on mount (default tab)
  useEffect(() => {
    if (!id) return;
    fetchDriverRecs();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDriverRecs = async () => {
    if (driverRecs) return;
    setRecLoading(true);
    setRecError(null);
    try {
      const data = await getDriverRecommendationsV9(id);
      setDriverRecs(data);
    } catch (err: any) {
      setRecError(err.message || 'Failed to load driver recommendations');
    } finally {
      setRecLoading(false);
    }
  };

  const fetchIronRecs = async () => {
    if (ironRecs) return;
    setRecLoading(true);
    setRecError(null);
    try {
      const data = await getIronRecommendationsV9(id);
      setIronRecs(data);
    } catch (err: any) {
      setRecError(err.message || 'Failed to load iron recommendations');
    } finally {
      setRecLoading(false);
    }
  };

  const fetchWedgeRecs = async () => {
    if (wedgeRecs) return;
    setRecLoading(true);
    setRecError(null);
    try {
      const data = await getWedgeRecommendationsRuleBased(id);
      setWedgeRecs(data);
    } catch (err: any) {
      setRecError(err.message || 'Failed to load wedge recommendations');
    } finally {
      setRecLoading(false);
    }
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setRecError(null);
    if (tab === 'driver') fetchDriverRecs();
    if (tab === 'irons') fetchIronRecs();
    if (tab === 'wedges') fetchWedgeRecs();
  };

  const formatDate = (dateString: string) => {
    const normalized = dateString.replace(' ', 'T');
    const utcString = normalized.endsWith('Z') ? normalized : normalized + 'Z';
    return new Date(utcString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-ink mx-auto" />
            <p className="mt-4 text-gray-600">Loading fitting...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">Fitting not found.</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 text-green-ink hover:text-green-ink font-medium">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb / Back */}
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-green-ink hover:text-green-ink font-medium flex items-center gap-1"
        >
          ← Back to My Fittings
        </button>

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Fitting Details</h1>
          {profile.created_at && (
            <p className="text-sm text-gray-500 mt-1">Submitted {formatDate(profile.created_at)}</p>
          )}
          <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Read-only
          </div>
        </div>

        {/* ── Profile Fields ─────────────────────────────── */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Your Profile</h2>
          <div className="space-y-3">
            {SECTIONS.map((section) => {
              const fields = section.fn(profile).filter(
                (f) => f.value !== null && f.value !== undefined && f.value !== ''
              );
              if (fields.length === 0) return null;
              return (
                <div key={section.name} className="bg-white rounded-lg shadow-sm border border-gray-100">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700">{section.name}</h3>
                  </div>
                  <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                    {fields.map((field) => (
                      <div key={field.label}>
                        <dt className="text-xs font-medium text-gray-400 uppercase tracking-wide">{field.label}</dt>
                        <dd className="mt-0.5 text-sm text-gray-900 font-medium">{field.value}</dd>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Recommendation Results ─────────────────────── */}
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Recommendations</h2>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex gap-6">
              {(['driver', 'irons', 'wedges'] as Tab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-green-ink text-green-ink'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'irons' ? 'Irons' : tab === 'wedges' ? 'Wedges' : 'Driver'}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          {activeTab === 'driver' && (
            <MLRecommendationResults
              data={driverRecs}
              loading={recLoading}
              error={recError}
              onBack={() => {}}
              sessionId={sessionIdRef.current}
            />
          )}
          {activeTab === 'irons' && (
            ironRecs ? (
              <MLRecommendationResults
                data={ironRecs}
                loading={recLoading}
                error={recError}
                onBack={() => {}}
                sessionId={sessionIdRef.current}
              />
            ) : (
              <div className="text-center py-12 text-gray-500">
                {recLoading ? (
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-ink mx-auto" />
                ) : recError ? (
                  <p className="text-red-600">{recError}</p>
                ) : (
                  <p>Iron recommendations loading...</p>
                )}
              </div>
            )
          )}
          {activeTab === 'wedges' && (
            wedgeRecs ? (
              <WedgeRecommendationResults
                data={wedgeRecs}
                loading={recLoading}
                error={recError}
                onBack={() => {}}
              />
            ) : (
              <div className="text-center py-12 text-gray-500">
                {recLoading ? (
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-ink mx-auto" />
                ) : recError ? (
                  <p className="text-red-600">{recError}</p>
                ) : (
                  <p>Wedge recommendations loading...</p>
                )}
              </div>
            )
          )}
        </section>
      </main>
    </div>
  );
};

export default FittingHistoryPage;
